import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { QrCode, ScanLine, CheckCircle2, AlertTriangle, Printer, Camera, CameraOff } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { CameraScanner } from "@/components/CameraScanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { LEAD, fmtDT, today } from "@/lib/constants";
import { PageHeader } from "@/components/Common";
import { StatusBadge } from "@/components/Badges";
import { CrudPage } from "@/components/CrudPage";
import { HotelSegment, useStrictHotel } from "@/components/HotelSegment";
import { useAuth } from "@/context/AuthContext";

const CP_FIELDS = [
  { key: "hotel_id", label: "Struttura", type: "hotel", required: true },
  { key: "code", label: "Codice QR", required: true },
  { key: "name", label: "Nome punto di controllo", required: true },
  { key: "zone", label: "Zona", type: "select", options: ["Interno", "Esterno"] },
  { key: "frequency", label: "Passaggi al giorno", type: "number", required: true },
];
const CP_COLS = [
  { key: "code", label: "Codice", render: (r) => <span className="font-mono text-amber-600">{r.code}</span> },
  { key: "name", label: "Punto di controllo" },
  { key: "zone", label: "Zona" },
  { key: "frequency", label: "Passaggi/giorno" },
  { key: "qr", label: "QR", render: (r) => <div className="bg-white p-1 rounded border border-slate-200 inline-block"><QRCodeSVG value={r.code} size={48} /></div> },
];

const ScanDialog = ({ open, onClose, checkpoints, hotelId, onDone }) => {
  const [code, setCode] = useState("");
  const [outcome, setOutcome] = useState("ok");
  const [note, setNote] = useState("");
  const [camera, setCamera] = useState(false);
  useEffect(() => { if (open) { setCode(""); setOutcome("ok"); setNote(""); } else setCamera(false); }, [open]);
  const onCameraResult = (text) => {
    const c = text.trim().toUpperCase();
    setCode(c);
    setCamera(false);
    toast.success(`QR letto: ${c}`);
  };
  const onCameraError = (msg) => {
    setCamera(false);
    toast.error(`Fotocamera non disponibile: ${msg}. Consenti l'accesso alla fotocamera o inserisci il codice.`);
  };
  const submit = async () => {
    if (!code) return toast.error("Scansiona o inserisci un codice");
    try {
      const { data } = await api.post("/rounds/scan", { hotel_id: hotelId, code, outcome, note });
      toast.success(`Checkpoint registrato: ${data.checkpoint_name}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-slate-200 max-w-md" data-testid="qr-scan-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">Scansione checkpoint</DialogTitle>
          <DialogDescription>Inquadra il QR del punto di controllo con la fotocamera, oppure selezionalo o inserisci il codice.</DialogDescription>
        </DialogHeader>
        {camera ? (
          <div className="space-y-2">
            <CameraScanner onResult={onCameraResult} onError={onCameraError} />
            <Button variant="outline" className="w-full gap-2" onClick={() => setCamera(false)} data-testid="qr-camera-stop-btn"><CameraOff className="w-4 h-4" />Chiudi fotocamera</Button>
          </div>
        ) : (
          <>
            <div className="relative aspect-video rounded-lg bg-slate-50 border border-slate-200 overflow-hidden grid place-items-center">
              <div className="absolute inset-6 border-2 border-amber-500/60 rounded-lg" />
              <div className="absolute left-8 right-8 h-0.5 bg-amber-400 shadow-[0_0_12px_#f59e0b] scan-line" />
              <div className="font-mono text-lg text-amber-700 z-10" data-testid="qr-scan-code-display">{code || "In attesa del QR…"}</div>
            </div>
            <Button className="w-full gap-2" onClick={() => setCamera(true)} data-testid="qr-camera-start-btn"><Camera className="w-4 h-4" />Usa fotocamera</Button>
          </>
        )}
        <div className="flex flex-wrap gap-1.5">
          {checkpoints.map((c) => (
            <button key={c.id} onClick={() => setCode(c.code)} data-testid={`qr-simulate-${c.code}`}
              className={`px-2 py-1 rounded text-[11px] font-mono border ${code === c.code ? "border-amber-500 text-amber-700" : "border-slate-300 text-slate-500 hover:border-slate-400"}`}>{c.code}</button>
          ))}
        </div>
        <Input placeholder="Codice manuale (es. MXP-CP01)" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="qr-code-input" />
        <div className="grid grid-cols-2 gap-2">
          <Button variant={outcome === "ok" ? "default" : "outline"} onClick={() => setOutcome("ok")} className="gap-1" data-testid="qr-outcome-ok"><CheckCircle2 className="w-4 h-4" />Tutto regolare</Button>
          <Button variant={outcome === "anomalia" ? "destructive" : "outline"} onClick={() => setOutcome("anomalia")} className="gap-1" data-testid="qr-outcome-anomaly"><AlertTriangle className="w-4 h-4" />Anomalia</Button>
        </div>
        {outcome === "anomalia" && <Textarea placeholder="Descrivi l'anomalia rilevata" value={note} onChange={(e) => setNote(e.target.value)} data-testid="qr-note-input" />}
        <Button onClick={submit} className="w-full gap-2" data-testid="qr-scan-simulate-button"><ScanLine className="w-4 h-4" />Registra passaggio</Button>
      </DialogContent>
    </Dialog>
  );
};

const CheckpointCard = ({ c }) => {
  const done = Math.min(c.scans_today, c.frequency);
  return (
    <div className="panel p-4" data-testid={`checkpoint-card-${c.code}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-mono text-[11px] text-amber-600">{c.code}</div>
          <div className="text-sm text-slate-900 font-medium">{c.name}</div>
        </div>
        <div className="bg-white p-1 rounded border border-slate-200 shrink-0" data-testid={`checkpoint-qr-${c.code}`}><QRCodeSVG value={c.code} size={56} /></div>
      </div>
      <Progress value={(done / c.frequency) * 100} className="h-1.5 mt-3" />
      <div className="flex justify-between text-xs mt-2 text-slate-500">
        <span>{c.scans_today}/{c.frequency} passaggi</span>
        <span>{c.last_scan ? fmtDT(c.last_scan.scanned_at).slice(-5) : "—"}</span>
      </div>
      {c.last_scan?.outcome === "anomalia" && <div className="mt-2"><StatusBadge value="anomalia" /></div>}
    </div>
  );
};

const PrintDialog = ({ open, onClose, checkpoints }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white" data-testid="qr-print-modal">
      <DialogHeader>
        <DialogTitle className="font-heading">QR code dei checkpoint</DialogTitle>
        <DialogDescription>Stampa e applica ogni QR nel relativo punto di controllo.</DialogDescription>
      </DialogHeader>
      <div className="print-area grid grid-cols-2 sm:grid-cols-3 gap-4">
        {checkpoints.map((c) => (
          <div key={c.id} className="border border-slate-300 rounded-lg p-4 flex flex-col items-center text-center break-inside-avoid">
            <QRCodeSVG value={c.code} size={150} />
            <div className="font-mono text-sm mt-3 text-slate-900">{c.code}</div>
            <div className="text-xs text-slate-600">{c.name}</div>
          </div>
        ))}
      </div>
      <Button onClick={() => window.print()} className="gap-2 w-full" data-testid="qr-print-btn"><Printer className="w-4 h-4" />Stampa</Button>
    </DialogContent>
  </Dialog>
);

export default function Ronde() {
  const { user } = useAuth();
  const [local, setLocal] = useState(null);
  const hid = useStrictHotel(local);
  const [status, setStatus] = useState(null);
  const [scans, setScans] = useState([]);
  const [open, setOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);

  const load = useCallback(async () => {
    if (!hid) return;
    const [s, r] = await Promise.all([api.get("/rounds/status", { params: { hotel_id: hid } }),
      api.get("/round_scans", { params: { hotel_id: hid, limit: 25 } })]);
    setStatus(s.data);
    setScans(r.data);
  }, [hid]);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <PageHeader eyebrow={`Ronde di oggi · ${today()}`} title="Ronde con checkpoint QR" subtitle="Ogni passaggio certifica data, ora e operatore. KPI target ronde completate ≥ 98%.">
        <Button variant="outline" className="gap-2" onClick={() => setPrintOpen(true)} data-testid="print-qr-btn"><QrCode className="w-4 h-4" />Stampa QR</Button>
        <Button className="gap-2" onClick={() => setOpen(true)} data-testid="start-scan-btn"><ScanLine className="w-4 h-4" />Scansiona QR</Button>
      </PageHeader>
      <PrintDialog open={printOpen} onClose={() => setPrintOpen(false)} checkpoints={status?.checkpoints || []} />
      <HotelSegment value={hid} onChange={setLocal} />
      <Tabs defaultValue="oggi">
        <TabsList>
          <TabsTrigger value="oggi" data-testid="tab-rounds-today">Stato ronde</TabsTrigger>
          <TabsTrigger value="checkpoint" data-testid="tab-checkpoints">Checkpoint</TabsTrigger>
        </TabsList>
        <TabsContent value="oggi" className="mt-4 space-y-4">
          {status && (
            <div className="panel p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="rounds-completion">
              <div className="font-heading text-4xl font-bold text-amber-600" data-testid="rounds-completion-value">{status.completion}%</div>
              <div className="flex-1">
                <div className="eyebrow mb-2">Completamento giornaliero · {status.done}/{status.planned} passaggi</div>
                <Progress value={status.completion} className="h-2" />
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {status?.checkpoints.map((c) => <CheckpointCard key={c.id} c={c} />)}
          </div>
          <div className="panel p-5" data-testid="recent-scans">
            <div className="eyebrow mb-3">Ultimi passaggi registrati</div>
            {scans.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-3 py-2 border-b border-slate-200 last:border-0 text-sm">
                <div className="min-w-0">
                  <span className="font-mono text-xs text-amber-600 mr-2">{s.code}</span>
                  <span className="text-slate-800">{s.checkpoint_name}</span>
                  {s.note && <div className="text-xs text-red-600">{s.note}</div>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs text-slate-500 hidden sm:inline">{s.operator}</span>
                  <span className="font-mono text-xs text-slate-500">{fmtDT(s.scanned_at)}</span>
                  <StatusBadge value={s.outcome} />
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="checkpoint" className="mt-4">
          <CrudPage resource="checkpoints" title="Checkpoint" testid="checkpoints" fields={CP_FIELDS} columns={CP_COLS} writeRoles={LEAD}
            newLabel="Nuovo checkpoint" defaults={{ frequency: 3, zone: "Interno" }} onChanged={load} />
        </TabsContent>
      </Tabs>
      {user && <ScanDialog open={open} onClose={() => setOpen(false)} checkpoints={status?.checkpoints || []} hotelId={hid} onDone={() => { setOpen(false); load(); }} />}
    </>
  );
}
